import { useEffect, useRef } from "react";
import { sendWrite } from "../../offline/writeQueue";
import { track } from "../../lib/events";
import type { ContentItem } from "../../types";

// How a completion came about: the app completed the item by its rule, or the
// teacher pressed "Mark complete". (Submitting a quiz or form is recorded by
// the server.)
export type CompletionSource = "auto" | "manual";

export interface Completion {
  done: boolean;
  /** The teacher's own "Mark complete". Rule-driven completion goes through the report* callbacks. */
  complete: (extraPercent?: number) => Promise<void>;
  reportMediaProgress: (percent: number) => void;
  reportDownload: () => void;
  reportResponseSubmitted: () => void;
}

// useCompletion drives every content type's completion behaviour from its
// admin-configured completion_rule, so "mark complete" isn't the only path
// and block_next enforcement (server + here) stays consistent no matter how
// completion was actually triggered.
export function useCompletion(
  item: ContentItem,
  moduleID: string,
  onComplete: () => void
): Completion {
  // Stamped on mount rather than at render time so the reported duration
  // measures the time the item was actually on screen.
  const opened = useRef(0);
  const done = item.progress.status === "completed";
  const timeReported = useRef(false);
  // `done` only flips after the write returns and the module reloads. Until
  // then a progress callback (media_threshold fires on every timeupdate past
  // the threshold) would send the completion again, and the server adds the
  // reported time each time.
  const completing = useRef(false);

  async function finish(source: CompletionSource, extraPercent?: number): Promise<void> {
    if (done || completing.current) return;
    completing.current = true;
    // Queued when offline; the item still counts as done on this device.
    try {
      await sendWrite({
        method: "PUT",
        path: `/api/teacher/progress/${item.id}`,
        body: {
          status: "completed",
          percent: extraPercent ?? 100,
          time_spent_seconds: Math.round((Date.now() - (opened.current || Date.now())) / 1000),
          completion_source: source,
          // The phone's clock: a queued offline write reaches the server later.
          completed_at: new Date().toISOString(),
        },
      });
    } catch (cause) {
      // A refused write must be retryable on the next progress callback.
      completing.current = false;
      throw cause;
    }
    track("item_completed", {
      module_id: moduleID,
      item_id: item.id,
      payload: { completion_source: source, completion_rule: item.completion_rule },
    });
    onComplete();
  }

  const complete = (extraPercent?: number) => finish("manual", extraPercent);

  useEffect(() => {
    opened.current = Date.now();
    completing.current = false;
    if (item.completion_rule === "viewed" && !done) {
      void finish("auto");
    }
    if (item.completion_rule === "time_spent" && !done) {
      const thresholdSeconds =
        item.completion_threshold || (item.estimated_minutes || 0) * 60 || 60;
      const timer = setTimeout(() => {
        if (!timeReported.current) {
          timeReported.current = true;
          void finish("auto");
        }
      }, thresholdSeconds * 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [item.id]);

  function reportMediaProgress(percent: number): void {
    if (item.completion_rule === "media_threshold" && !done) {
      const threshold = item.completion_threshold ?? 90;
      if (percent >= threshold) void finish("auto", 100);
    }
  }

  function reportDownload(): void {
    if (item.completion_rule === "download_requested" && !done) void finish("auto");
  }

  function reportResponseSubmitted(): void {
    if (item.completion_rule === "response_submitted" && !done) void finish("auto");
  }

  return { done, complete, reportMediaProgress, reportDownload, reportResponseSubmitted };
}
